(ns client-portal.client.voice-commerce.orders.drawer.views
  "The order detail panel — the right-hand drawer a row click opens, registered as
   `:voice-commerce/order-panel` (see `client-portal.client.layout/panel-right-view`)
   and mirroring the sessions drawer."
  (:require [clojure.string :as s]
            [client-portal.client.anonymous.data :as anon.d]
            [client-portal.client.layout :as l]
            [client-portal.client.voice-commerce.orders.components :as orders.c]
            [client-portal.client.voice-commerce.orders.data :as orders.d]
            [shared.core.amelia-light.components.components :as alc]
            [shared.core.components.components :as c]
            [shared.core.reactive :as re]
            [shared.core.style :as style]
            [shared.core.util :as util]
            [shared.core.utils.currency :as currency]))

(defn no-message-block [{:keys [message]}]
  [:div {:style {:display :flex
                 :align-items :center
                 :justify-content :center
                 :padding "30px 16px"
                 :height "100%"
                 :width "100%"}}
   [:span {:style {:font-size 13
                   :line-height 1.4
                   :font-weight 500
                   :color (style/alc-color "Text/secondary")
                   :text-align :center
                   :padding "16px"}}
    message]])

(defn ->order-detail [order]
  (if (or (nil? order) (util/loading? order))
    :loading
    @(re/subscribe [:voice-commerce/order-by-id- order])))

(defn header []
  (let [copied-link?* (re/ratom false)
        concept-id- (re/subscribe [:concepts/current-concept-id-])
        selected-order- (re/subscribe [:voice-commerce/selected-order-])]
    (fn []
      (let [selected-order @selected-order-]
        [:div {:style {:background-color (style/alc-color "Background/secondary")
                       :flex "0 0 auto"
                       :display :flex
                       :gap 8
                       :padding "12px 16px"
                       :position :sticky
                       :top 0
                       :min-height 58
                       :box-sizing :border-box
                       :z-index (inc (style/z :medium))}}
         [:span {:style {:color (style/alc-color "Text/primary_header")
                         :display :flex
                         :align-items :center
                         :font-size 20
                         :font-weight 400
                         :line-height 1.4}}
          "Order Detail"]
         [c/spacer]
         (when (:order-id selected-order)
           ^{:key (str "share-" @copied-link?*)}
           [alc/tooltip
            {:tooltip-content (if @copied-link?*
                                "Copied"
                                "Copy Order Link")
             :target-content [alc/up-ghost-button {:icon-type (if @copied-link?* :CheckCircleIcon :CopyIcon)
                                                   :on-click #(do
                                                                (re/dispatch [:core/copy-to-clipboard
                                                                              (orders.d/sharable-order-url
                                                                                @concept-id-
                                                                                selected-order)
                                                                              {:notify? false}])
                                                                (reset! copied-link?* true)
                                                                (util/wait 1500 (fn [] (reset! copied-link?* false))))}]}])
         [alc/up-button {:icon-type :CrossIcon
                         :rounded? true
                         :on-click #(re/dispatch [:voice-commerce/orders-toggle-drawer-open {:open? false}])}]]))))

(defn order-overview
  "The order's id, then its time flanked by the prev/next arrows — the same block the
   sessions drawer puts under its header."
  []
  (let [copied-order-id?* (re/ratom false)
        selected-order- (re/subscribe [:voice-commerce/selected-order-])
        orders- (re/subscribe [:voice-commerce/orders-list-flattened-])
        next-page-loading?- (re/subscribe [:voice-commerce/orders-list-next-page-loading?-])]
    (fn []
      (let [selected-order @selected-order-
            selected-order-idx (:order-idx selected-order)
            orders @orders-
            first? (= selected-order-idx 0)
            last? (when selected-order-idx
                    (>= selected-order-idx (dec (count orders))))
            order-id (:order-id selected-order)
            order-detail (->order-detail selected-order)
            occurred-at (:occurred-at (util/when-not-loading order-detail))
            loading? (util/loading? order-detail)]
        [:div {:style {:background-color (style/alc-color "Background/secondary")
                       :flex "0 0 auto"
                       :display :flex
                       :flex-direction :column
                       :align-items :center
                       :gap 4
                       :padding "12px 0"}}
         (when order-id
           ^{:key (str "order-id-" @copied-order-id?*)}
           [alc/tooltip
            {:tooltip-content (if @copied-order-id?*
                                "Copied"
                                "Copy Order ID")
             :target-content [c/interactive
                              :div {:on-click #(do
                                                 (re/dispatch [:core/copy-to-clipboard
                                                               order-id
                                                               {:notify? false}])
                                                 (reset! copied-order-id?* true)
                                                 (util/wait 1500 (fn [] (reset! copied-order-id?* false))))}
                              [:div {:style (cond-> {:color (style/alc-color "Text/secondary")
                                                     :display :flex
                                                     :align-items :center
                                                     :justify-content :center
                                                     :gap 9}
                                                    (not @copied-order-id?*)
                                                    (assoc :cursor :pointer))}
                               [:span {:style {:color :inherit
                                               :font-size 14
                                               :font-weight 400
                                               :line-height 1.43}}
                                order-id]
                               [c/material-icon {:icon-type (if @copied-order-id?*
                                                              :check-circle
                                                              :content-copy)
                                                 :style {:color (style/alc-color "Icons/secondary")
                                                         :font-size 14}}]]]}])
         [:div {:style {:display :flex
                        :align-items :center
                        :justify-content :space-between
                        :padding "0 16px"
                        :width "100%"}}
          (if selected-order-idx
            (if first?
              [:div {:style {:flex "0 0 36px"}}]
              [alc/up-ghost-button {:icon-type :ArrowLeftIcon
                                    :disabled @next-page-loading?-
                                    :on-click #(re/dispatch [:voice-commerce/orders-move-order :prev])}])
            [c/spacer])
          (cond
            loading?
            [:div {:style {:display :flex
                           :align-items :center
                           :padding "4px 0"}}
             [c/loader-rectangle {:height 28
                                  :width 160
                                  :style {:border-radius 8}}]]

            occurred-at
            [:div {:style {:display :flex
                           :align-items :center}}
             [:span {:style {:color (style/alc-color "Text/primary_header")
                             :font-size 20
                             :font-weight 400
                             :line-height 1.4}}
              occurred-at]])
          (if selected-order-idx
            (if (or (not last?) @next-page-loading?-)
              [alc/up-ghost-button {:icon-type :ArrowRightIcon
                                    :disabled @next-page-loading?-
                                    :on-click #(re/dispatch [:voice-commerce/orders-move-order :next])}]
              [:div {:style {:flex "0 0 36px"}}])
            [c/spacer])]]))))

(defn masked-phone
  "Only the last four digits show; a number too short to have them is shown as-is."
  [phone]
  (let [digits (util/just-da-digits phone)]
    (cond
      (empty? (str phone)) "-"
      (< (count digits) 10) (str phone)
      :else (str "+1 (•••) •••-" (subs digits (- (count digits) 4))))))

(defn detail-row [label value]
  (when value
    [:div {:style {:display :flex
                   :align-items :center
                   :gap 12}}
     [:span {:style {:color (style/alc-color "Text/secondary")
                     :flex "0 0 119px"
                     :font-size 14
                     :line-height 1.43}}
      label]
     [:div {:style {:color (style/alc-color "Text/primary")
                    :flex 1
                    :display :flex
                    :align-items :center
                    :font-size 14
                    :line-height 1.43}}
      value]]))

(defn order-info [{:keys [restaurant-name guest-name guest-phone pickup-wait-minutes source customer-type]}]
  (let [text-or-dash #(if (not-empty (str %)) (str %) "-")]
    [:div {:style {:display :flex
                   :flex-direction :column
                   :gap 16
                   :padding "4px 16px 16px"}}
     [detail-row "Location" (some-> restaurant-name (anon.d/location-label) (text-or-dash))]
     [detail-row "Guest" (some-> guest-name (text-or-dash))]
     [detail-row "Phone" (some-> guest-phone (masked-phone))]
     [detail-row "Pickup wait" (some-> pickup-wait-minutes (util/readable-number) (str " min"))]
     [detail-row "Order Type" [orders.c/source-cell source]]
     [detail-row "Customer Type" [orders.c/source-cell customer-type]]]))

(defn status-badge [status]
  (let [{:keys [icon-type color]} (get orders.c/order-status->icon (some-> status (str) (s/lower-case)))]
    [:div {:style {:padding "0 16px 16px"}}
     [:div {:style {:background-color (style/mix-color (style/alc-color (or color "Background/tertiary")) 30)
                    :border (str "1px solid " (style/alc-color (or color "Border/divider")))
                    :border-radius 8
                    :display :inline-flex
                    :align-items :center
                    :gap 6
                    :padding "6px 12px 6px 6px"}}
      (when icon-type
        [alc/material-icon {:icon-type icon-type
                            :style {:color (style/alc-color (or color "Text/primary"))
                                    :font-size 16}}])
      [:span {:style {:color (style/alc-color "Text/primary")
                      :font-size 12
                      :line-height 1.33}}
       (if (not-empty (str status))
         (util/->capitalize-words status)
         "-")]]]))

(defn modifier-label [{:keys [name quantity total-cents]}]
  (str name
       (when (some-> quantity (> 1))
         (str " (" quantity ")"))
       (when (some-> total-cents (pos?))
         (str " (" (currency/currency-int->readable-price total-cents) ")"))))

(defn item-row [{:keys [name quantity unit-price-cents modifiers]}]
  (let [quantity (or quantity 1)
        modifiers-cents (->> modifiers (keep :total-cents) (reduce + 0))
        line-cents (when unit-price-cents
                     (* quantity (+ unit-price-cents modifiers-cents)))]
    [:div {:style {:display :flex
                   :flex-direction :column
                   :gap 4}}
     [:div {:style {:display :flex
                    :align-items :flex-start
                    :justify-content :space-between
                    :gap 16}}
      [:span {:style {:color (style/alc-color "Text/primary")
                      :font-size 14
                      :line-height 1.43}}
       (str quantity " x " (or name "-"))]
      [:span {:style {:color (style/alc-color "Text/secondary")
                      :flex "0 0 auto"
                      :font-size 14
                      :line-height 1.43}}
       (if line-cents
         (currency/currency-int->readable-price line-cents)
         "-")]]
     (when (not-empty modifiers)
       (into [:div {:style {:display :flex
                            :flex-direction :column
                            :gap 2
                            :padding-left 20}}]
             (map (fn [modifier]
                    [:span {:style {:color (style/alc-color "Text/secondary")
                                    :font-size 13
                                    :line-height 1.4}}
                     (modifier-label modifier)]))
             modifiers))]))

(defn items-section [items]
  [:div {:style {:flex "0 0 auto"
                 :display :flex
                 :flex-direction :column
                 :gap 12
                 :padding "16px 16px 0"}}
   [:span {:style {:color (style/alc-color "Text/secondary")
                   :font-size 12
                   :font-weight 600
                   :letter-spacing 0.5
                   :line-height 1.33
                   :text-transform :uppercase}}
    "Items"]
   (if (not-empty items)
     (into [:div {:style {:display :flex
                          :flex-direction :column
                          :gap 10}}]
           (map (fn [item]
                  ^{:key (:line-item-id item)}
                  [item-row item]))
           items)
     [:span {:style {:color (style/alc-color "Text/secondary")
                     :font-size 14
                     :line-height 1.43}}
      "No items"])])

(defn totals-row [{:keys [label value primary? total?]}]
  [:div {:style {:display :flex
                 :align-items :center
                 :justify-content :space-between
                 :gap 16}}
   [:span {:style {:color (style/alc-color (if (or primary? total?) "Text/primary" "Text/secondary"))
                   :font-size (if total? 16 14)
                   :line-height 1.43}}
    label]
   [:span {:style {:color (style/alc-color (cond
                                             total? "Common/White"
                                             primary? "Text/primary"
                                             :else "Text/secondary"))
                   :font-size (if total? 18 14)
                   :font-weight (if total? 600 400)
                   :line-height 1.43}}
    value]])

(defn totals-divider []
  [:div {:style {:border-top (str "1px solid " (style/alc-color "Border/divider"))}}])

(defn totals-section [{:keys [subtotal discount tax delivery-fee tip base-commission discovery sponsored total]}]
  (let [commission-pct (when (and (some-> subtotal (pos?)) base-commission)
                         (-> base-commission (/ subtotal) (* 100) (js/Math.round)))
        fee-rows (cond-> [{:label "Tax" :value (some-> tax (orders.c/money-label))}
                          {:label "Delivery Fee" :value (some-> delivery-fee (orders.c/money-label))}
                          {:label "Tip" :value (some-> tip (orders.c/money-label))}
                          {:label "Incremental Order Fee" :value (when (number? sponsored)
                                                                   (orders.c/money-label sponsored))}
                          {:label "Discovery Fee" :value (when (number? discovery)
                                                           (orders.c/money-label discovery))}
                          {:label (str "Base Commission" (when commission-pct
                                                           (str " (" commission-pct "%)")))
                           :value (or (some-> base-commission (orders.c/money-label))
                                      "-")}]
                         (some-> discount (pos?))
                         (conj {:label "Discount" :value (str "-" (orders.c/money-label discount))}))]
    [:div {:style {:flex "0 0 auto"
                   :padding 16}}
     [:div {:style {:background-color (style/alc-color "Background/tertiary")
                    :border-radius 12
                    :display :flex
                    :flex-direction :column
                    :gap 12
                    :padding "16px 20px"}}
      [totals-row {:label "Subtotal"
                   :value (orders.c/money-label subtotal)
                   :primary? true}]
      [totals-divider]
      (->> fee-rows
           (filter :value)
           (map (fn [row]
                  ^{:key (:label row)}
                  [totals-row row]))
           (into [:div {:style {:display :flex
                                :flex-direction :column
                                :gap 10}}]))
      [totals-divider]
      [totals-row {:label "Total"
                   :value (orders.c/money-label total)
                   :total? true}]]]))

(defn order-details [order]
  [:<>
   [:div {:style {:background-color (style/alc-color "Background/secondary")
                  :flex "0 0 auto"
                  :display :flex
                  :flex-direction :column}}
    [order-info order]
    [:div {:style {:border-top (str "1px solid " (style/alc-color "Border/divider"))
                   :flex "0 0 auto"
                   :margin "0 16px 16px"}}]
    [status-badge (:order-status order)]]
   [items-section (:items order)]
   [:div {:style {:flex 1}}]
   [totals-section order]])

(defmethod l/panel-right-view
  :voice-commerce/order-panel [_]
  (let [selected-order @(re/subscribe [:voice-commerce/selected-order-])
        order-detail (->order-detail selected-order)
        loading? (util/loading? order-detail)
        order (util/when-not-loading order-detail)]
    ;; Always a column: the panel stretches this to its full height, and the spacer
    ;; in `order-details` needs a flex parent to push the totals to the bottom when
    ;; the order is shorter than the panel.
    [:div {:id :order-drawer-scroll-container
           :style {:overflow :auto
                   :display :flex
                   :flex-direction :column}}
     [header]
     [order-overview]
     (cond
       loading?
       [alc/up-loader-with-label {:label "Loading Order..."
                                  :style {:flex 1
                                          :height :auto
                                          :min-height :auto}}]

       ;; A 404 (or any failure) transforms to an empty map — the order the URL names
       ;; isn't there to show.
       (empty? order)
       [no-message-block {:message (str "No Order Found For \"" (:order-id selected-order) "\"")}]

       :else
       [order-details order])]))
